import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Spin, Empty } from 'antd';
import { Popover, type PopoverPlacement } from './Popover';
import { SquareCommentItem } from './SquareCommentItem';
import { SquareCommentInput } from './SquareCommentInput';
import { useSquareComments } from '../hooks/useSquareComments';
import { useAuth } from '../contexts/AuthContext';
import '../../styles/SquareCommentModal.css';

export interface SquareCommentModalProps {
  /** 触发器元素 */
  children: React.ReactNode;
  /** 帖子 ID */
  postId: string;
  /** 评论数量变化回调 */
  onCommentCountChange?: (count: number) => void;
  /** 弹窗位置 */
  placement?: PopoverPlacement;
}

/**
 * 评论弹窗组件（自定义 Popover 版本）
 */
export const SquareCommentModal: React.FC<SquareCommentModalProps> = ({
  children,
  postId,
  onCommentCountChange,
  placement = 'bottomRight',
}) => {
  const { user } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const {
    comments,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    addComment,
    removeComment,
  } = useSquareComments({
    postId,
    onCommentCountChange,
  });

  // Load initial comments when popover opens
  useEffect(() => {
    if (open && comments.length === 0) {
      loadMore();
    }
  }, [open, comments.length, loadMore]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!open || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const trigger = loadMoreTriggerRef.current;
    if (trigger) {
      observer.observe(trigger);
    }

    return () => {
      if (trigger) {
        observer.unobserve(trigger);
      }
    };
  }, [open, hasMore, loadingMore, loadMore]);

  const handleAddComment = useCallback(
    async (content: string) => {
      return await addComment(content);
    },
    [addComment]
  );

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      await removeComment(commentId);
    },
    [removeComment]
  );

  const isEmpty = !loading && comments.length === 0;

  const content = (
    <div className="square-comment-panel">
      {/* Comment List */}
      <div className="square-comment-list-container">
        {loading ? (
          <div className="square-comment-loading">
            <Spin />
          </div>
        ) : isEmpty ? (
          <div className="square-comment-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无评论"
            />
          </div>
        ) : (
          <div className="square-comment-list" ref={scrollRef}>
            {comments.map((comment) => (
              <SquareCommentItem
                key={comment.id}
                comment={comment}
                currentUserId={user?.id}
                onDelete={handleDeleteComment}
              />
            ))}
            {/* Load more trigger */}
            {hasMore && (
              <div
                ref={loadMoreTriggerRef}
                className="square-comment-load-more"
              >
                {loadingMore && <Spin size="small" />}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comment Input */}
      <div className="square-comment-input-container">
        <SquareCommentInput
          user={user}
          onSubmit={handleAddComment}
          loading={loading}
        />
      </div>

      {/* Error message */}
      {error && <div className="square-comment-error">{error}</div>}
    </div>
  );

  return (
    <Popover
      content={content}
      open={open}
      onOpenChange={setOpen}
      placement={placement}
      contentClassName="square-comment-popover"
    >
      {children}
    </Popover>
  );
};

export default SquareCommentModal;
